import { IAllocationDimensionValue, IAllocationDimensionValueFields } from "helpers/allocation-dimension";
import { DoorsValueGroupedById, IAttributeData, IAttributeDataChanges, UnitByDoor } from "./types";
import {
    ALTERNATIVE_KEY_COLUMN_FIELD,
    AttributeNameSanitizerExpression,
    IN_ASSORTMENT_FIELD,
    KEY_COLUMN_FIELD,
    NAME_FIELD,
    NOTES_COLUMN_FIELD,
} from "./constants";
import { nullish } from "helpers/nullish";
import { ISchemaKey } from "helpers/product-collection";
import cloneDeep from "lodash/cloneDeep";
import { buildColumnExpression } from "helpers/utils/build-column-expression";

export const isValidAttributeValue = (value: string) =>
    nullish(value) || value === "" || /^([A-Z]|[0-9])[A-Z0-9 ]*([A-Z]|[0-9])*$/i.test(value);

export const getColumnName = (data: IAttributeData, fieldName: string) => {
    const headers = Object.keys(data || {});
    return headers.find((header) => {
        const expression = buildColumnExpression(header);
        return expression.test(fieldName);
    });
};

export const parseBooleanValue = (value: string) => {
    if (nullish(value) || value === "" || ["no", "n", "false"].includes(`${value}`.toString())) {
        return false;
    }

    if (["yes", "y", "true"].includes(`${value}`.toString())) {
        return true;
    }

    return null;
};

export const convertValueToStringAndLowerCase = (value: any) => {
    if (typeof value === "string") {
        return value?.toLowerCase();
    }

    return value?.toString().toLowerCase();
};

const addHeaderSuffixIfNeeded = (
    headers: string[],
    header: string,
    originalHeader: string,
    index: number,
    iteration: number = 1
) => {
    const searchExpression = buildColumnExpression(header);
    if (headers.find((name, idx) => (index === -1 || idx < index) && searchExpression.test(name))) {
        return addHeaderSuffixIfNeeded(headers, `${originalHeader}_${iteration}`, originalHeader, -1, iteration + 1);
    }

    return header;
};

export const sanitizeHeaderNames = (headers: string[]) => {
    return headers.reduce((acc, header, index) => {
        if (acc.length === 0) {
            acc.push(header);
        } else {
            acc.push(addHeaderSuffixIfNeeded([...acc, ...headers.slice(index)], header, header, index));
        }
        return acc;
    }, []);
};

export const MAX_FILE_SIZE = 2 * 1024 * 1024;

type ParseCustomAttributeValueToUpdateProps = {
    attributeName: string;
    customAttributes: any;
    fields: IAllocationDimensionValueFields;
    rows: IAllocationDimensionValue[];
    row: IAllocationDimensionValue;
    rowIndex: number;
};

type ParseCustomAttributeValueToUpdateResult = {
    newValuesToUpdate: { oldValue?: any; shouldDeleteOldValueFromAllowedValues?: boolean; field: string; value: any };
    rowsWithNewValues: IAllocationDimensionValue[];
};

export const parseCustomAttributeValueToUpdate = ({
    attributeName,
    customAttributes,
    fields,
    rows,
    row,
    rowIndex,
}: ParseCustomAttributeValueToUpdateProps): ParseCustomAttributeValueToUpdateResult => {
    const thisFieldExists = rows.some((row) => row.fields?.hasOwnProperty(attributeName));
    const cloneRows = [...rows];

    cloneRows.splice(rowIndex, 1, {
        ...row,
        fields: {
            ...fields,
            [attributeName]: {
                string_value: customAttributes[attributeName]?.toString(),
            },
        },
    });
    const isOldValueNotEmptyString = fields[attributeName]?.string_value.trim() !== "";
    const isOldValueDifferentOfNewValue =
        fields[attributeName]?.string_value !== customAttributes[attributeName]?.toString();
    const oldValueNotIsUsedInOtherRows = cloneRows.every(
        (row) =>
            row?.fields[attributeName]?.string_value?.toLocaleLowerCase() !==
            fields[attributeName]?.string_value.toLocaleLowerCase()
    );
    const shouldDeleteOldValueFromAllowedValues =
        thisFieldExists && isOldValueNotEmptyString && isOldValueDifferentOfNewValue && oldValueNotIsUsedInOtherRows;

    const valuesToRemove = {
        oldValue: fields[attributeName]?.string_value,
        shouldDeleteOldValueFromAllowedValues,
    };

    return {
        newValuesToUpdate: {
            field: attributeName,
            value: customAttributes[attributeName]?.toString(),
            ...(shouldDeleteOldValueFromAllowedValues && valuesToRemove),
        },
        rowsWithNewValues: cloneRows,
    };
};

export const getEditableDoorAttributesData = (
    doorAttributesData: IAttributeData[],
    readOnlyColumns: string[]
): IAttributeData[] => {
    const inAssortmentFieldName = getColumnName(doorAttributesData[0], IN_ASSORTMENT_FIELD);
    const lowerCaseReadOnlyColumns = readOnlyColumns.map((column) => column.toLowerCase());

    return doorAttributesData.map((item) => {
        const editableColumns = Object.keys(item || {})
            .filter((key) => !lowerCaseReadOnlyColumns.includes(key.toString().toLowerCase()))
            .reduce(
                (target, key) => ({
                    ...target,
                    [key]: item[key],
                }),
                {}
            );

        if (inAssortmentFieldName) {
            editableColumns[inAssortmentFieldName] = parseBooleanValue(
                convertValueToStringAndLowerCase(item[inAssortmentFieldName])
            );
        }

        return editableColumns;
    });
};

export const sanitizeAttributeHeader = (header: string) =>
    header
        .replace(AttributeNameSanitizerExpression, " ")
        .replace(/\s{2,}/g, " ")
        .trim();

export const sanitizeAttributeKey = (key: string) =>
    key
        .toLocaleLowerCase()
        .replace(AttributeNameSanitizerExpression, " ")
        .replace(/\s{2,}/g, " ")
        .trim()
        .replace(/\s+/g, "_");

export const getSanitizedAttributesData = (data: IAttributeData[]): any[] => {
    const headers = Object.keys(data[0] || {}).map((header) => [header, sanitizeAttributeKey(header)]);
    function translateItem(item) {
        return headers.reduce((accumulator, [header, sanitizedKey]) => {
            accumulator[sanitizedKey] = item[header];
            return accumulator;
        }, {});
    }

    return data.map(translateItem);
};

export const removeRawColumns = (data: IAttributeData[], columns: RegExp[]): any[] => {
    const headers = Object.keys(data[0] || {});
    function translateItem(item) {
        return headers.reduce((accumulator, header) => {
            if (!columns.some((it) => it.test(header))) {
                accumulator[header] = item[header];
            }
            return accumulator;
        }, {});
    }

    return data.map(translateItem);
};

export const getDoorsAttributesDataChanges = ({
    doorAttributesData,
    readOnlyAttributes = [],
    totalUnitsByDoorCodeId,
    existingDoorAttributes,
    doorsValueGroupedByCodeId,
    onSetSanitizedAttributesData,
}: {
    doorAttributesData: IAttributeData[];
    readOnlyAttributes: string[];
    totalUnitsByDoorCodeId: UnitByDoor;
    existingDoorAttributes: ISchemaKey[];
    doorsValueGroupedByCodeId: DoorsValueGroupedById;
    onSetSanitizedAttributesData: (value: any) => void;
}): IAttributeDataChanges => {
    const columnsToCreate = [];
    const columnsToUpdate = [];
    const doorsForDisableOrEnable = [];
    const doorsForDisableOrEnableWithUnits = [];
    const customAttributesNameToUpdate = [];
    const editableDoorsAttributesData = getEditableDoorAttributesData(doorAttributesData, [
        ...readOnlyAttributes,
        NOTES_COLUMN_FIELD,
        NAME_FIELD,
    ]);
    const sanitizedAttributesData = getSanitizedAttributesData(editableDoorsAttributesData);
    const columnsHeadersMap = new Map<string, string>(
        Object.keys(editableDoorsAttributesData[0]).map((header) => [
            sanitizeAttributeKey(header),
            sanitizeAttributeHeader(header),
        ])
    );

    sanitizedAttributesData.forEach((attributesData) => {
        const { code_id: codeId, in_assortment, ...customAttributes } = attributesData;
        const doorGroupedById = doorsValueGroupedByCodeId[Number(codeId)];

        if (doorGroupedById?.row) {
            const {
                row: { included, fields, name },
            } = doorGroupedById;

            // When the door will be turn on/off
            if (in_assortment !== undefined && included !== in_assortment) {
                const doorToChanges = { id: codeId, name, in_assortment };

                !doorsForDisableOrEnable.includes(doorToChanges) && doorsForDisableOrEnable.push(doorToChanges);

                const doorHasUnits = totalUnitsByDoorCodeId[codeId];
                // When the door with units will be turn on/off
                if (doorHasUnits) {
                    const doorWithUnitsToChanges = {
                        id: codeId,
                        name,
                        units: totalUnitsByDoorCodeId[codeId],
                        in_assortment,
                    };
                    !doorsForDisableOrEnableWithUnits.includes(doorWithUnitsToChanges) &&
                        doorsForDisableOrEnableWithUnits.push(doorWithUnitsToChanges);
                }
            }
            // Verify if a custom attribute's value was changed
            Object.keys(customAttributes).forEach((attributeName) => {
                const currentAttributeValue = fields[attributeName]?.string_value?.toLowerCase();
                const newAttributeValue = customAttributes[attributeName]?.toString()?.toLowerCase();

                if (currentAttributeValue !== newAttributeValue) {
                    !customAttributesNameToUpdate.includes(attributeName) &&
                        customAttributesNameToUpdate.push(attributeName);
                }
            });
        }
    });

    // Validate if custom attribute should be created or updated
    customAttributesNameToUpdate.forEach((attributeName) => {
        const attributeNameNormalized = attributeName?.replace("_", " ").toLowerCase();
        const existingCustomAttribute = existingDoorAttributes.find((key) => {
            const listOfAttributeNames = [key.field_name?.toLowerCase(), key.display_name?.toLowerCase()];
            const hasAttributeName = listOfAttributeNames.includes(attributeName?.toLowerCase());
            const hasAttributeNameNormalized = listOfAttributeNames.includes(attributeNameNormalized);
            return hasAttributeName || hasAttributeNameNormalized;
        });
        if (existingCustomAttribute) {
            !columnsToUpdate.includes(attributeName) && columnsToUpdate.push(attributeName);
        } else {
            !columnsToCreate.includes(attributeName) && columnsToCreate.push(attributeName);
        }
    });

    onSetSanitizedAttributesData(sanitizedAttributesData);

    return {
        columnsToUpdate,
        columnsToCreate,
        doorsForDisableOrEnable,
        columnsHeadersMap,
        doorsForDisableOrEnableWithUnits,
    };
};

type GetFieldNameToCustomValueListProps = {
    sanitizedAttributesData: any[];
    doorsValueGroupedByCodeId: DoorsValueGroupedById;
    dimensionValues: IAllocationDimensionValue[];
    onAddRowToUpdate: (rowIndex: number, row: IAllocationDimensionValue) => void;
};

type GetFieldNameToCustomValueListReturn = {
    oldValue?: any;
    shouldDeleteOldValueFromAllowedValues?: boolean;
    field: string;
    value: any;
}[];

export const getFieldNameToCustomValueList = ({
    sanitizedAttributesData,
    doorsValueGroupedByCodeId,
    dimensionValues,
    onAddRowToUpdate,
}: GetFieldNameToCustomValueListProps): GetFieldNameToCustomValueListReturn => {
    const fieldNameToCustomValueList = [];
    const rowIndexForUpdate: number[] = [];

    let cloneRows = cloneDeep(dimensionValues);

    sanitizedAttributesData.forEach((data) => {
        const { code_id, in_assortment, ...customAttributes } = data;

        const rowData = doorsValueGroupedByCodeId[Number(code_id)];

        if (rowData?.row) {
            const { row, rowIndex } = rowData;

            const { fields, included } = row;

            const activeIsDifferent = in_assortment !== undefined && included !== in_assortment;

            Object.keys(customAttributes).forEach((attributeName) => {
                const currentAttributeValue = fields[attributeName]?.string_value?.toLowerCase();
                const newAttributeValue = customAttributes[attributeName]?.toString()?.toLowerCase();

                if (currentAttributeValue !== newAttributeValue) {
                    const { newValuesToUpdate, rowsWithNewValues } = parseCustomAttributeValueToUpdate({
                        attributeName,
                        customAttributes,
                        fields,
                        rows: cloneRows,
                        row,
                        rowIndex,
                    });

                    cloneRows = rowsWithNewValues;

                    fieldNameToCustomValueList.push(newValuesToUpdate);
                    rowIndexForUpdate.push(rowIndex);
                }

                row.fields[attributeName] = {
                    string_value: customAttributes[attributeName]?.toString(),
                };
            });

            const shouldUpdateCurrentRow = rowIndexForUpdate.some((index) => index === rowIndex) || activeIsDifferent;
            if (shouldUpdateCurrentRow) {
                onAddRowToUpdate(rowIndex, row);
            }
        }
    });

    return fieldNameToCustomValueList;
};

export const updateDoorAttributesDataWithAlternativeId = (doorAttributesData: IAttributeData[]): IAttributeData[] => {
    const headers = Object.keys(doorAttributesData[0] || {});
    const hasDefaultKeyColumn = headers.find((header) => {
        const expression = buildColumnExpression(header);
        return expression.test(KEY_COLUMN_FIELD);
    });
    const hasAlternativeKeyColumn = headers.find((header) => {
        const expression = buildColumnExpression(header);
        return expression.test(ALTERNATIVE_KEY_COLUMN_FIELD);
    });

    const data = [];
    if (!hasDefaultKeyColumn) {
        if (hasAlternativeKeyColumn) {
            for (const doorAttributeData of doorAttributesData) {
                doorAttributeData[KEY_COLUMN_FIELD] = doorAttributeData[ALTERNATIVE_KEY_COLUMN_FIELD];
                delete doorAttributeData[ALTERNATIVE_KEY_COLUMN_FIELD];
                data.push(doorAttributeData);
            }
            return data;
        }
    }
    return doorAttributesData;
};
